import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import AddBoxIcon from "@mui/icons-material/AddBox";
import RecipeSelector from "./RecipeSelector";
import Switch from "@mui/material/Switch";
import NewScenario from "./NewScenario";
import DeleteDialog from "../common/DeleteDialog";
import { GlobalMessageContext } from "../app/App";
import ConfigData from "../config/config.json";
import Tooltip from "@mui/material/Tooltip";

const webserver = ConfigData.WEBSERVER;

const label = { inputProps: { "aria-label": "Switch demo" } };

const collator = new Intl.Collator("de", { sensitivity: "base" });

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (orderBy === "title" || orderBy === "description") {
    //return a[orderBy].localeCompare(b[orderBy], 'de', {sensitivity: 'base'})
    return collator.compare(a[orderBy], b[orderBy]);
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "title",
    numeric: false,
    disablePadding: false,
    label: "Titel",
  },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: "Beschreibung",
  },
  {
    id: "recipes",
    numeric: false,
    disablePadding: false,
    label: "Mögliche Rezepturen",
  },
  {
    id: "active",
    numeric: false,
    disablePadding: false,
    label: "Aktiv",
  },
];

function EnhancedTableHead(props) {
  const [open, setOpen] = React.useState(false);
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align="right">
            <Tooltip title={"Szenario anlegen"} enterDelay={1000}>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ color: "#00aa00", float: "right" }}
              >
                <AddBoxIcon />
              </IconButton>
            </Tooltip>
        </TableCell>
        <NewScenario
          open={open}
          setOpen={setOpen}
          setRows={props.setRowss}
          rowss={props.rowss}
        />
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function Scenarios() {
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("title");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const { setGlobalMessage } = React.useContext(GlobalMessageContext);

  const [rowss, setRowss] = useState([]);

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/scenario/getAll",
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        for (var i = 0; i < data.length; i++) {
          data[i].descriptionExpanded = false;
          data[i].titleExpanded = false;
          data[i].deleteOpen = false;
        }

        data.forEach((scenario, index) => {
          let temp = [];

          scenario.recipes.forEach((recipe, index) => {
            temp[index] = recipe.title;
          });
          scenario.recipes = temp;
        });

        setRowss(data);
      })
      .catch((error) => {});
  }, []);

  const deleteScenario = (index, id) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/scenario/delete/" +
        id,
      { method: "DELETE", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRowss((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy.splice(index, 1);
          return rowsCopy;
        });
        setGlobalMessage({
          globalMessage: "Szenario gelöscht",
          severity: "success",
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const setScenarioActive = (index, id, activity) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/scenario/setActivity/" +
        id +
        "?activity=" +
        activity,
      { method: "PUT", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        if (data) {
          setRowss((prev) => {
            let rowsCopy = JSON.parse(JSON.stringify(prev));
            rowsCopy[index].active = activity;
            return rowsCopy;
          });
          setGlobalMessage({
            globalMessage: "Änderung erfolgreich",
            severity: "success",
          });
        } else {
          setGlobalMessage({
            globalMessage: "Fehler: Keine Rezepturen in Szenario",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const updateScenarioDescription = (index, id, description) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/scenario/setDescription/" +
        id,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: description,
      }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRowss((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy[index].description = description;
          return rowsCopy;
        });
        setGlobalMessage({
          globalMessage: "Änderung erfolgreich",
          severity: "success",
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const updateScenarioTitle = (index, id, title) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/scenario/setTitle/" +
        id,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: title,
      }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRowss((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy[index].title = title;
          return rowsCopy;
        });
        setGlobalMessage({
          globalMessage: "Änderung erfolgreich",
          severity: "success",
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  const checkRecipes = (recipes) => {
    let includes = false;

    recipes.forEach((recipe) => {
      if (recipe.toLowerCase().includes(searchQuery.toLowerCase())) {
        includes = true;
      }
    });
    return includes;
  };

  const filteredRows = rowss.filter((row) => {
    return (
      row.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.description
        .toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      checkRecipes(row.recipes)
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rowss.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRowss={setRowss}
              rowss={rowss}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, pageIndex) => {
                  let index = 0;

                  for (let found = 0; found < rowss.length; found++) {
                    if (rowss[found].id === row.id) {
                      index = found;
                      break;
                    }
                  }
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.id}>
                      <TableCell id={labelId} scope="row">
                        <TextField
                          sx={{ minWidth: 150, m: 0 }}
                          size="small"
                          multiline={row.titleExpanded}
                          type="text"
                          id="newScenarioTitle"
                          value={row.title}
                          onInput={(e) => {
                            setRowss((prev) => {
                              let temp = JSON.parse(JSON.stringify(prev));
                              temp[index].title = e.target.value;
                              return temp;
                            });
                          }}
                          onBlur={() => {
                            updateScenarioTitle(
                              index,
                              row.id,
                              rowss[index].title
                            );
                          }}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={(e) => {
                                  setRowss((prev) => {
                                    let temp = JSON.parse(JSON.stringify(prev));
                                    temp[index].titleExpanded =
                                      !temp[index].titleExpanded;
                                    return temp;
                                  });
                                }}
                              >
                                {row.titleExpanded ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            ),
                          }}
                        />
                      </TableCell>
                      {/*<TableCell style={{whiteSpace: "normal", wordBreak: "break-word"}} align="left"> {row.descriptionExpanded ? row.email : row.email.substring(0, 65) + " ..."}<IconButton size=''>{row.descriptionExpanded ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}</IconButton></TableCell>*/}
                      <TableCell align="left">
                        <TextField
                          sx={{ width: 450, m: 0 }}
                          size="small"
                          multiline={row.descriptionExpanded}
                          margin="dense"
                          id="newScenarioDescription"
                          label="Beschreibung"
                          type="text"
                          value={row.description}
                          required
                          onInput={(e) => {
                            setRowss((prev) => {
                              let temp = JSON.parse(JSON.stringify(prev));
                              temp[index].description = e.target.value;
                              return temp;
                            });
                          }}
                          onBlur={() => {
                            updateScenarioDescription(
                              index,
                              row.id,
                              rowss[index].description
                            );
                          }}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                sx={{ alignSelf: "flex-start" }}
                                onClick={(e) => {
                                  setRowss((prev) => {
                                    let temp = JSON.parse(JSON.stringify(prev));
                                    temp[index].descriptionExpanded =
                                      !temp[index].descriptionExpanded;
                                    return temp;
                                  });
                                }}
                              >
                                {row.descriptionExpanded ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )}
                              </IconButton>
                            ),
                          }}
                        />
                      </TableCell>
                      <TableCell align="left">
                        {" "}
                        <RecipeSelector
                          width={300}
                          selectedRecipes={row.recipes}
                          setSelectedRecipes={setRowss}
                          index={index}
                          id={row.id}
                        />{" "}
                      </TableCell>
                      <TableCell align="left" padding="none">
                        {" "}
                        <Switch
                          {...label}
                          checked={row.active}
                          onChange={() =>
                            setScenarioActive(index, row.id, !row.active)
                          }
                          sx={{ paddingLeft: 1.85 }}
                        />{" "}
                      </TableCell>
                      <TableCell align="right">
                          <Tooltip title={"Szenario löschen"} enterDelay={1000}>
                            <IconButton
                              onClick={() =>
                                setRowss((prev) => {
                                  let temp = JSON.parse(JSON.stringify(prev));
                                  temp[index].deleteOpen = true;
                                  return temp;
                                })
                              }
                            >
                              <DeleteIcon sx={{ color: "#b50202" }} />
                            </IconButton>
                          </Tooltip>
                      </TableCell>
                      <DeleteDialog
                        function={() => deleteScenario(index, row.id)}
                        open={
                          row.deleteOpen !== undefined ? row.deleteOpen : false
                        }
                        setOpen={() =>
                          setRowss((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[index].deleteOpen = false;
                            return temp;
                          })
                        }
                        title="Szenario löschen"
                        text={
                          'Sind Sie sicher, dass Sie das Szenario "' +
                          row.title +
                          '" unwiderruflich löschen wollen?'
                        }
                      />
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowss.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + " von " + count;
          }}
          labelRowsPerPage="Zeilen pro Seite"
        />
      </Paper>
    </Box>
  );
}
